import React, { useEffect, useState } from 'react';
import { Shield, Target, Users, TrendingUp, Award, Rocket, CheckCircle, Save, Edit, X, Upload, Plus, Trash2 } from 'lucide-react';
import Button from '../components/UI/Button';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { SiteContent } from '../types';
import EditableContent from '../components/Admin/EditableContent';

// Helper for Icons
const IconMap: any = {
  Shield: Shield,
  Target: Target,
  Rocket: Rocket,
  Users: Users,
  Award: Award,
};

const AboutPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [content, setContent] = useState<SiteContent | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const pageContent = await api.getPageContent();
      setContent(pageContent);
    };
    fetchData();
  }, []);

  const handleSaveContent = async () => {
    if (!content) return;
    setSaving(true);
    await api.updatePageContent(content);
    setSaving(false);
    setIsEditing(false);
  };

  const updateAboutContent = (section: keyof SiteContent['about'], key: string, value: any) => {
    if (!content) return;
    setContent({
      ...content,
      about: {
        ...content.about,
        [section]: {
          ...content.about[section],
          [key]: value
        }
      }
    });
  };

  const updateValueItem = (index: number, field: string, value: string) => {
    if (!content) return;
    const newItems = [...content.about.values.items];
    newItems[index] = { ...newItems[index], [field]: value };
    
    setContent({
      ...content,
      about: {
        ...content.about,
        values: {
            ...content.about.values,
            items: newItems
        }
      }
    });
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, section: 'mission' | 'team', index?: number) => {
    if (e.target.files && e.target.files[0] && content) {
       const file = e.target.files[0];
       try {
           const urls = await api.uploadFiles([file]);
           const uploadedUrl = urls[0];
           
           if (section === 'mission') {
               updateAboutContent('mission', 'imageUrl', uploadedUrl);
           } else if (section === 'team' && typeof index === 'number') {
               const newMembers = [...content.about.team.members];
               newMembers[index] = { ...newMembers[index], imageUrl: uploadedUrl };
               updateAboutContent('team', 'members', newMembers);
           }
       } catch (error) {
           console.error("Upload failed", error);
           alert("خطا در آپلود تصویر");
       }
    }
  };

  const updateTeamMember = (index: number, field: string, value: string) => {
      if (!content) return;
      const newMembers = [...content.about.team.members];
      newMembers[index] = { ...newMembers[index], [field]: value };
      updateAboutContent('team', 'members', newMembers);
  };

  const addTeamMember = () => {
      if (!content) return;
      const newMember = { name: 'نام عضو جدید', role: 'سمت شغلی', imageUrl: '/images/avatar-placeholder.png' };
      updateAboutContent('team', 'members', [...content.about.team.members, newMember]);
  };

  const removeTeamMember = (index: number) => {
      if (!content) return;
      const newMembers = content.about.team.members.filter((_, i) => i !== index);
      updateAboutContent('team', 'members', newMembers);
  };

  if (!content || !content.about) return <div className="min-h-screen flex items-center justify-center">در حال بارگذاری...</div>;

  const isAdmin = user?.role === 'admin';

  return (
    <div className="bg-white relative">
       {/* Admin Toolbar */}
       {isAdmin && (
        <div className="fixed bottom-6 left-6 z-50 flex gap-2">
            {isEditing ? (
                <>
                    <Button onClick={handleSaveContent} disabled={saving} className="shadow-xl bg-green-600 hover:bg-green-700 flex items-center gap-2">
                        <Save size={18}/> {saving ? 'ذخیره...' : 'ذخیره تغییرات'}
                    </Button>
                    <Button onClick={() => { setIsEditing(false); window.location.reload(); }} variant="ghost" className="bg-white shadow-xl text-red-500 hover:bg-red-50">
                        <X size={18}/>
                    </Button>
                </>
            ) : (
                <Button onClick={() => setIsEditing(true)} className="shadow-xl bg-gray-900 hover:bg-gray-800 flex items-center gap-2">
                    <Edit size={18}/> ویرایش صفحه
                </Button>
            )}
        </div>
      )}

      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
           <div className="absolute top-0 right-0 w-1/2 h-full bg-teal-900/20 transform skew-x-12 translate-x-20"></div>
           <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-600 rounded-full blur-3xl opacity-10"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-black mb-6 leading-tight">
             <EditableContent 
                isEditing={isEditing}
                value={content.about.hero.title}
                onChange={(val) => updateAboutContent('hero', 'title', val)}
             />
          </h1>
          <div className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto leading-relaxed">
             <EditableContent 
                isEditing={isEditing}
                type="textarea"
                value={content.about.hero.subtitle}
                onChange={(val) => updateAboutContent('hero', 'subtitle', val)}
                className="text-center bg-gray-800 border-gray-700 text-white"
             />
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 md:py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
          <div>
            <div className="inline-block px-3 py-1 bg-teal-50 text-teal-700 rounded-full text-sm font-bold mb-4 border border-teal-100">
               <EditableContent 
                  isEditing={isEditing}
                  value={content.about.mission.badge}
                  onChange={(val) => updateAboutContent('mission', 'badge', val)}
               />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-6 leading-tight">
               <EditableContent 
                  isEditing={isEditing}
                  type="textarea"
                  value={content.about.mission.title}
                  onChange={(val) => updateAboutContent('mission', 'title', val)}
               />
            </h2>
            <div className="text-gray-600 leading-relaxed mb-6 text-justify">
               <EditableContent 
                  isEditing={isEditing}
                  type="textarea"
                  value={content.about.mission.text1}
                  onChange={(val) => updateAboutContent('mission', 'text1', val)}
               />
            </div>
            <div className="text-gray-600 leading-relaxed mb-8 text-justify">
               <EditableContent 
                  isEditing={isEditing}
                  type="textarea"
                  value={content.about.mission.text2}
                  onChange={(val) => updateAboutContent('mission', 'text2', val)}
               />
            </div>
            
            <div className="grid grid-cols-2 gap-6">
                <div className="flex flex-col gap-2">
                    <h4 className="font-black text-3xl text-teal-600">
                        <EditableContent 
                           isEditing={isEditing}
                           value={content.about.mission.stats1Value}
                           onChange={(val) => updateAboutContent('mission', 'stats1Value', val)}
                        />
                    </h4>
                    <span className="text-sm text-gray-500">
                        <EditableContent 
                           isEditing={isEditing}
                           value={content.about.mission.stats1Label}
                           onChange={(val) => updateAboutContent('mission', 'stats1Label', val)}
                        />
                    </span>
                </div>
                <div className="flex flex-col gap-2">
                    <h4 className="font-black text-3xl text-purple-600">
                        <EditableContent 
                           isEditing={isEditing}
                           value={content.about.mission.stats2Value}
                           onChange={(val) => updateAboutContent('mission', 'stats2Value', val)}
                        />
                    </h4>
                    <span className="text-sm text-gray-500">
                        <EditableContent 
                           isEditing={isEditing}
                           value={content.about.mission.stats2Label}
                           onChange={(val) => updateAboutContent('mission', 'stats2Label', val)}
                        />
                    </span>
                </div>
            </div>
          </div>
          <div className="relative group">
             <div className="absolute inset-0 bg-teal-200 rounded-3xl transform rotate-3 scale-95 opacity-50"></div>
             <img 
               src={content.about.mission.imageUrl} 
               alt="Team collaboration" 
               className="relative rounded-3xl shadow-xl border-4 border-white object-cover h-[500px] w-full"
             />
             {isEditing && (
                <label className="absolute inset-0 z-10 flex items-center justify-center bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer rounded-3xl">
                    <div className="text-center">
                        <Upload size={40} className="mx-auto mb-2"/>
                        <span className="font-bold">تغییر تصویر</span>
                    </div>
                    <input type="file" className="hidden" accept="image/*" onChange={(e) => handleImageUpload(e, 'mission')} />
                </label>
             )}
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="bg-gray-50 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                    <EditableContent 
                        isEditing={isEditing}
                        value={content.about.values.title}
                        onChange={(val) => updateAboutContent('values', 'title', val)}
                    />
                </h2>
                <div className="text-gray-500">
                    <EditableContent 
                        isEditing={isEditing}
                        value={content.about.values.subtitle}
                        onChange={(val) => updateAboutContent('values', 'subtitle', val)}
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {content.about.values.items.map((item, idx) => {
                    const Icon = IconMap[item.icon] || Shield;
                    const colorClasses = [
                        'bg-teal-100 text-teal-600',
                        'bg-blue-100 text-blue-600',
                        'bg-purple-100 text-purple-600'
                    ];
                    return (
                        <div key={idx} className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow border border-gray-100">
                            <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 ${colorClasses[idx % 3]}`}>
                                {isEditing ? (
                                    <select 
                                        value={item.icon} 
                                        onChange={(e) => updateValueItem(idx, 'icon', e.target.value)}
                                        className="bg-transparent text-xs w-full text-center outline-none"
                                    >
                                        {Object.keys(IconMap).map(iconName => (
                                            <option key={iconName} value={iconName}>{iconName}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <Icon size={28}/>
                                )}
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-3">
                                <EditableContent 
                                    isEditing={isEditing}
                                    value={item.title}
                                    onChange={(val) => updateValueItem(idx, 'title', val)}
                                />
                            </h3>
                            <div className="text-gray-500 leading-relaxed">
                                <EditableContent 
                                    isEditing={isEditing}
                                    type="textarea"
                                    value={item.desc}
                                    onChange={(val) => updateValueItem(idx, 'desc', val)}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 md:py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
         <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
                <EditableContent 
                    isEditing={isEditing}
                    value={content.about.team.title}
                    onChange={(val) => updateAboutContent('team', 'title', val)}
                />
            </h2>
            <div className="text-gray-500">
                <EditableContent 
                    isEditing={isEditing}
                    value={content.about.team.subtitle}
                    onChange={(val) => updateAboutContent('team', 'subtitle', val)}
                />
            </div>
         </div>
         
         <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
            {content.about.team.members.map((member, idx) => (
                <div key={idx} className="text-center group relative">
                    {isEditing && (
                        <button onClick={() => removeTeamMember(idx)} className="absolute top-0 right-0 z-20 p-1.5 bg-red-100 text-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                            <Trash2 size={16}/>
                        </button>
                    )}
                    <div className="w-32 h-32 mx-auto mb-4 rounded-full overflow-hidden border-4 border-gray-50 shadow-lg group-hover:scale-105 transition-transform relative">
                        <img src={member.imageUrl} alt={member.name} className="w-full h-full object-cover bg-gray-100" />
                        {isEditing && (
                            <label className="absolute inset-0 bg-black/50 flex items-center justify-center cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity">
                                <Upload size={20} className="text-white"/>
                                <input type="file" className="hidden" accept="image/*" onChange={(e) => handleImageUpload(e, 'team', idx)}/>
                            </label>
                        )}
                    </div>
                    <h3 className="font-bold text-gray-900">
                        <EditableContent 
                            isEditing={isEditing}
                            value={member.name}
                            onChange={(val) => updateTeamMember(idx, 'name', val)}
                            className="text-center"
                        />
                    </h3>
                    <div className="text-sm text-teal-600">
                         <EditableContent 
                            isEditing={isEditing}
                            value={member.role}
                            onChange={(val) => updateTeamMember(idx, 'role', val)}
                            className="text-center text-xs"
                        />
                    </div>
                </div>
            ))}
            
            {isEditing && (
                <div 
                    onClick={addTeamMember}
                    className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-2xl h-48 cursor-pointer hover:bg-gray-50 transition-colors"
                >
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-2">
                        <Plus size={24} className="text-gray-400"/>
                    </div>
                    <span className="text-sm font-medium text-gray-500">افزودن عضو جدید</span>
                </div>
            )}
         </div>
      </section>

      {/* CTA */}
      <section className="bg-teal-600 py-16">
         <div className="max-w-4xl mx-auto px-4 text-center text-white">
            <h2 className="text-3xl font-bold mb-6">آماده شروع مسیر جدید خود هستید؟</h2>
            <p className="text-teal-100 mb-8 text-lg">
                چه به دنبال فروش کسب‌وکار خود باشید و چه در جستجوی فرصت‌های سرمایه‌گذاری ناب، Tbiz همراه شماست.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button onClick={() => navigate('/create-listing')} className="bg-white text-teal-700 hover:bg-gray-100 border-none">
                    ثبت آگهی جدید
                </Button>
                <Button variant="outline" onClick={() => navigate('/listings')} className="border-white text-white hover:bg-teal-700">
                    جستجوی فرصت‌ها
                </Button>
            </div>
         </div>
      </section>
    </div>
  );
};

export default AboutPage;